import styled, { css } from 'styled-components';

import { Input } from '@components';

type ContainerProps = {
  $hide: boolean;
  $disabled: boolean;
};

export const Container = styled.form<ContainerProps>`
  ${({ $hide, $disabled }) => css`
    display: ${$hide ? 'none' : 'flex'};
    flex-direction: column;
    height: 100%;
    opacity: ${$disabled ? 0.6 : 1};
    pointer-events: ${$disabled ? 'none' : 'all'};
  `}
`;

export const Header = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid ${theme.colors.background[80]};
  `}
`;

export const HeaderTitle = styled.h3`
  ${({ theme }) => css`
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
  `}
`;

export const HeaderBadge = styled.span`
  ${({ theme }) => css`
    margin-left: auto;
    font-size: 0.8125rem;
    font-weight: 500;
    color: ${theme.colors.brand.primary.default};
  `}
`;

export const FilterTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
`;

type FilterTabProps = {
  $active: boolean;
};

export const FilterTab = styled.button<FilterTabProps>`
  ${({ theme, $active }) => css`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    font-weight: 500;
    border-radius: 1.25rem;
    cursor: pointer;
    transition: all 0.2s ease;

    ${$active
      ? css`
          background: ${theme.colors.brand.primary.default};
          color: #fff;
          border: 1px solid ${theme.colors.brand.primary.default};
        `
      : css`
          background: transparent;
          color: ${theme.colors.foreground[60]};
          border: 1px solid ${theme.colors.background[60]};

          &:hover {
            border-color: ${theme.colors.brand.primary.default};
            color: ${theme.colors.brand.primary.default};
          }
        `}

    svg {
      width: 1rem;
      height: 1rem;
    }
  `}
`;

export const MessageContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 1rem;
    color: ${theme.colors.foreground[60]};
    text-align: center;
    padding: 2rem;

    svg {
      opacity: 0.5;
    }

    p {
      margin: 0;
      font-size: 0.95rem;
    }
  `}
`;

export const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
`;

type QuestionCardProps = {
  $highlighted?: boolean;
};

export const QuestionCard = styled.div<QuestionCardProps>`
  ${({ theme, $highlighted }) => css`
    width: 100%;
    padding: 0.875rem;
    margin: 0 0.5rem 0.75rem 0.5rem;
    max-width: calc(100% - 1rem);
    background: ${$highlighted
      ? theme.colors.brand.primary.opaque
      : theme.colors.background[100]};
    border-radius: 0.75rem;
    border: 1px solid ${theme.colors.background[40]};
    border-left: ${$highlighted
      ? `3px solid ${theme.colors.brand.primary.default}`
      : `1px solid ${theme.colors.background[40]}`};
    display: flex;
    gap: 0.75rem;
    box-sizing: border-box;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      border-color: ${theme.colors.brand.primary.default};
      ${!$highlighted &&
      css`
        border-left-color: ${theme.colors.brand.primary.default};
      `}
    }
  `}
`;

export const AvatarWrapper = styled.div`
  ${({ theme }) => css`
    width: 2.5rem;
    height: 2.5rem;
    min-width: 2.5rem;
    border-radius: 50%;
    background: ${theme.colors.brand.primary.default};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    color: #fff;
  `}
`;

export const QuestionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
`;

export const QuestionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

export const UserName = styled.span`
  ${({ theme }) => css`
    font-size: 0.875rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
  `}
`;

export const TimeStamp = styled.span`
  ${({ theme }) => css`
    font-size: 0.75rem;
    color: ${theme.colors.foreground[60]};
  `}
`;

export const QuestionText = styled.p`
  ${({ theme }) => css`
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: ${theme.colors.foreground[100]};
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: pre-wrap;
  `}
`;

export const VoteWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
`;

type VoteButtonProps = {
  $voted: boolean;
};

export const VoteButton = styled.button<VoteButtonProps>`
  ${({ theme, $voted }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s ease;

    ${$voted
      ? css`
          background: ${theme.colors.brand.primary.default};
          border: none;
          color: #fff;
        `
      : css`
          background: transparent;
          border: 1px solid ${theme.colors.foreground[40]};
          color: ${theme.colors.foreground[60]};

          &:hover {
            border-color: ${theme.colors.brand.primary.default};
            color: ${theme.colors.brand.primary.default};
          }
        `}

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }

    &:active {
      transform: scale(0.95);
    }
  `}
`;

export const VoteCount = styled.span<VoteButtonProps>`
  ${({ theme, $voted }) => css`
    font-size: 0.75rem;
    font-weight: 600;
    color: ${$voted
      ? theme.colors.brand.primary.default
      : theme.colors.foreground[60]};
  `}
`;

export const QuestionInput = styled(Input)`
  ${({ theme }) => css`
    height: 3.25rem;
    max-width: calc(100% - 2rem);
    margin: 0 1rem 1rem 1rem;
    flex-shrink: 0;
    background: ${theme.colors.background[80]};
    border-radius: 0.5rem;

    input {
      font-size: 0.875rem;
    }
  `}
`;
